import { Plus } from 'lucide-react';
import * as React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  CompactBar,
  CompactChipLine,
  CompactDivider,
  CompactTitle,
  DataTable,
  HelpPopover,
  PageHeader,
  SplitList,
  STICKY_TABLE_TOP,
  StatusChip,
  useScrolledPast,
  useWorkToolbar,
  WorkToolbar,
  type DataTableState,
  type IDataColumn,
  WorkControls,
  openRow,
  PaneControl,
  RowOpens,
  usePanePrefs,
  useOpenRecord
} from '@/design';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type { IListImport } from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import type { IPage } from '../../api/oversight-contracts';
import {
  LoadError,
  PastTheEnd,
  SectionTabs,
  useAdminData,
  type IAdminTab
} from '../../components/admin-page';
import { ListArea, useListRereads, usePaneRead } from '../../components/list-area';
import type { ICardLinkState } from '../../components/participant-link';
import { countOf, IMPORT_STATES, LIST_SURVEYS, importStateChip, listSurveyLabel } from '../../lists';
import { NEW_IMPORT, TEAM_PATHS, importPath } from '../../paths';
import { refusalMessage } from '../../refusal';
import { CompactTabs, PageAction, useKnownFunnels, type IKnownFilter } from '../lists/work-page/light-list';
import { WorkPageHeaderActions, useReadAt } from '../lists/work-page/header-actions';
import {
  WorkPager,
  dropPaging,
  readItemPages,
  usePerPage,
  useWorkPaging,
  windowOf
} from '../lists/work-page/pager';
import { RecordSearch } from '../record-lists/record-work-page';

/**
 * Import (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §5.1) — the
 * Data Import Wizard with the dry run survey-api gives: every import survey-api holds, `GET
 * /admin/imports`, behind `firm_exchange.manage` (every route of it, the reads included — the rail's
 * entry and this screen's gate). An import opens beside the list (`AdminImportsArea`), and **New
 * import** is the wizard drawn where the import will be (`/team/import/new`, `NEW_IMPORT`).
 *
 * **A light work page** (SR-515, the work pages design, "Three forms"): the header row with its tabs,
 * the key number, *Refresh* handed to the app bar and the "?"; the search (`q`: the file name or the
 * importer's email, read as typed); Survey and State as the columns' funnels, one value each, of the
 * known vocabularies — a value no funnel knows still sent and drawn as spelled; the filter line; the
 * table; the work pages' pager. No cards, no counts read. The list is the URL (`q`, `survey`,
 * `state`, `page`, `size`); a 400 `invalid-query` is said in survey-api's words.
 *
 * **Nothing here polls**: the import's record polls while the Worker is at it, and has the list read
 * again when its state moves (`useListReread`).
 *
 * **The Thresholds tab** (§5.5, Q6) is `internal_fields.author`'s — a tab of its own address, drawn
 * for a holder alone (`importTabs`).
 */

/** survey-api's page ceiling on imports. */
const IMPORTS_MAX_PAGE = 100;

/** The two tabs of Import, each for the permission its route names; none drawn while only one is
 *  the reader's — a lone tab names nothing to choose between. */
export function useImportTabs(): IAdminTab[] | undefined {
  const can = useCan();
  const tabs: IAdminTab[] = [
    ...(can('firm_exchange.manage') ? [{ to: TEAM_PATHS.imports, label: 'Imports', end: true }] : []),
    ...(can('internal_fields.author') ? [{ to: TEAM_PATHS.importThresholds, label: 'Thresholds' }] : [])
  ];
  return tabs.length > 1 ? tabs : undefined;
}

/** The list's filters as survey-api takes them, from the address. */
function importsFilter(params: URLSearchParams) {
  return {
    q: params.get('q') || undefined,
    survey: params.get('survey') || undefined,
    state: params.get('state') || undefined
  };
}

/** One page of `pageSize` imports under the address's filters. */
async function readImports(
  params: URLSearchParams,
  page: number,
  pageSize: number,
  signal: AbortSignal
): Promise<IPage<IListImport>> {
  try {
    const answer = await listsApi.imports({ ...importsFilter(params), page, pageSize }, signal);
    return { items: answer.imports, page, pageSize, total: answer.total };
  } catch (error) {
    throw new Error(refusalMessage(error, 'The imports could not be read'));
  }
}

/** The window the address names, in the viewer's Per page: one read where it can be. */
function readWindow(
  params: URLSearchParams,
  page: number,
  perPage: number,
  pages: number,
  signal: AbortSignal
): Promise<IPage<IListImport>> {
  return readItemPages(
    (at, size) => readImports(params, at, size, signal),
    page,
    perPage,
    pages,
    IMPORTS_MAX_PAGE
  );
}

const IMPORT_FILTERS: readonly IKnownFilter[] = [
  {
    param: 'survey',
    column: 'survey',
    label: 'Survey',
    options: LIST_SURVEYS.map((one) => ({ value: one.code, label: one.label })),
    labelOf: listSurveyLabel
  },
  {
    param: 'state',
    column: 'state',
    label: 'State',
    options: IMPORT_STATES.map((one) => ({ value: one, label: importStateChip(one).label })),
    labelOf: (state) => importStateChip(state).label
  }
];

/** The list's keys Clear all drops — the search and the funnels. */
const CLEARED = ['q', 'survey', 'state'];

export function AdminImportsPage() {
  const location = useLocation();
  // Beside the list as *Opens in* says — its column, or a sheet over this page (SR-530).
  const openRecord = useOpenRecord();
  const [params, setParams] = useSearchParams();
  const tabs = useImportTabs();
  const rereads = useListRereads();
  const q = params.get('q') ?? '';
  const filter = importsFilter(params);
  const paging = useWorkPaging();
  const { page, perPage, pages } = paging;
  const asked = JSON.stringify([filter, page, perPage, pages]);
  const filtered = filter.survey !== undefined || filter.state !== undefined;
  const { data, error, reading, reload } = useAdminData(
    async (signal) => ({
      asked,
      searched: q !== '',
      filtered,
      page: await readWindow(params, page, perPage, pages, signal)
    }),
    [asked, rereads]
  );
  // The last answer while the next is out; nothing once this query's read has failed.
  const failed = error !== null && !reading;
  const shown = failed && data?.asked !== asked ? null : data;
  const readAt = useReadAt(data);

  /** A change of search or filter starts at the top. */
  const update = (mutate: (next: URLSearchParams) => void, replace = false) =>
    setParams(
      (current) => {
        const next = dropPaging(new URLSearchParams(current));
        mutate(next);
        return next;
      },
      { replace }
    );
  const search = (text: string) => {
    if (text === q) void reload();
    else update((next) => (text === '' ? next.delete('q') : next.set('q', text)), true);
  };
  const choose = (name: string, value: Nullable<string>) =>
    update((next) => (value === null ? next.delete(name) : next.set(name, value)));
  const clearAll = () =>
    update((next) => {
      for (const name of CLEARED) next.delete(name);
    });
  const funnels = useKnownFunnels(IMPORT_FILTERS, params, choose);
  const beside = (entry: IListImport) =>
    openRecord(importPath(entry.id), { search: location.search, list: true } satisfies ICardLinkState);
  // A row's press follows *Opens in*: beside the list, or the import in a new tab.
  const prefs = usePanePrefs();
  const openImport = (entry: IListImport) => openRow(prefs, importPath(entry.id), () => beside(entry));

  const tableState: DataTableState | undefined =
    shown === null
      ? failed
        ? { kind: 'error', content: <LoadError message={error} /> }
        : { kind: 'loading' }
      : shown.page.items.length > 0
        ? undefined
        : {
            kind: 'empty',
            content:
              shown.page.total > 0 ? (
                <PastTheEnd
                  page={shown.page.page}
                  pageSize={shown.page.pageSize}
                  total={shown.page.total}
                  counted={['import', 'imports']}
                  onPage={paging.setPage}
                />
              ) : (
                emptyText(shown.searched, shown.filtered)
              )
          };

  const total = shown?.asked === asked ? shown.page.total : null;
  const refresh = { reading, readAt, onClick: () => void reload() };
  const help = (
    <HelpPopover label="About Import" align="end">
      <p>
        A file a firm sends back is checked first: every change it would make is listed, and an
        unusual one is held. Nothing is written until the check is applied, and an applied import
        can be reversed as one.
      </p>
      <p>
        The search reads as you type: each word is found in the file&apos;s name or the email of
        whoever imported it. The Survey and State columns&apos; funnels narrow the list to one value
        each. The newest import comes first.
      </p>
      <p>
        A row opens beside the list by its first icon and in a new tab by ↗; a click on the row goes
        where «Opens in» says.
      </p>
    </HelpPopover>
  );
  const newImport = (
    <PageAction
      label="New import"
      icon={<Plus className="size-4" aria-hidden />}
      to={importPath(NEW_IMPORT, filter.survey ? `?${new URLSearchParams({ survey: filter.survey })}` : '')}
      state={{ search: location.search, list: true } satisfies ICardLinkState}
    />
  );
  const searchBox = (compact: boolean) => (
    <RecordSearch
      q={q}
      label="Filter imports"
      placeholder="Filter imports…"
      onSearch={search}
      compact={compact}
    />
  );
  const toolbar = useWorkToolbar();
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);
  const chips = funnels.chips;

  return (
    <>
      <PageHeader
        title="Import"
        row
        tabs={tabs && <SectionTabs tabs={tabs} />}
        actions={
          <WorkPageHeaderActions
            summary={total === null ? null : countOf(total, 'import')}
            summaryData={{ 'data-imports-summary': '' }}
            refresh={refresh}
            help={help}
          />
        }
      />
      <CompactBar
        shown={scrolled}
        label="Import, compact"
        actions={<WorkPageHeaderActions summary={null} refresh={refresh} help={help} />}
        below={chips.length > 0 && <CompactChipLine chips={chips} onClearAll={clearAll} />}
      >
        <CompactTitle>Import</CompactTitle>
        {tabs && <CompactTabs label="Section" tabs={tabs} />}
        <CompactDivider />
        {searchBox(true)}
        <span className="flex-1" />
        <CompactDivider />
        {newImport}
        <CompactDivider />
      </CompactBar>
      <div className="grid min-w-0 gap-3 px-6 py-4">
        <WorkControls end={setControlsEnd}>
          <WorkToolbar
            toolbar={toolbar}
            status={{
              reading,
              count: shown ? `${shown.page.total.toLocaleString('en-US')} found` : undefined
            }}
            search={searchBox(false)}
            quick={null}
            quickCount={0}
            end={
              <>
                {newImport}
                <PaneControl prefs={prefs} kind="record" />
              </>
            }
          />
          {chips.length > 0 && <CompactChipLine label="Filters" chips={chips} onClearAll={clearAll} />}
        </WorkControls>

        {failed && shown !== null && (
          <LoadError message={`Could not refresh — showing the imports as last read. ${error}`} />
        )}

        <DataTable
          label="Imports"
          columns={importColumns(funnels.header, beside)}
          rows={tableState ? null : (shown?.page.items ?? null)}
          rowKey={(entry) => entry.id}
          rowProps={(entry) => ({ 'data-import': entry.id }) as React.HTMLAttributes<HTMLTableRowElement>}
          onOpen={openImport}
          busy={reading && shown !== null}
          state={tableState}
          minWidth="64rem"
          stickyTop={STICKY_TABLE_TOP}
        />

        {shown && <WorkPager {...paging.pager} total={shown.page.total} busy={reading} sticky />}
      </div>
    </>
  );
}

/** What an empty answer says — of the query it answers, so "none matches" never reads as "none". */
function emptyText(searched: boolean, filtered: boolean): string {
  if (searched && filtered) return 'No import matches this search with these filters.';
  if (searched) return 'No import matches this search.';
  if (filtered) return 'No import matches these filters.';
  return 'No file has been imported yet — New import checks one.';
}

/** An import's state as its chip — `[data-import-state]` is what a spec reads. */
export function ImportStateChip({ state }: { state: string }) {
  const chip = importStateChip(state);
  return (
    <span data-import-state={state}>
      <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
    </span>
  );
}

/** A count the check has not made yet — the Worker is still reading the file — is not a zero. */
function counted(entry: IListImport, value: number): React.ReactNode {
  return entry.state === 'queued' ? <span className="text-muted-foreground">—</span> : value;
}

/** The table's columns, Survey and State headed by their funnels; the ways in first — the import
 *  beside the list, and ↗ in a new tab. */
function importColumns(
  head: (filter: IKnownFilter) => React.ReactNode,
  beside: (entry: IListImport) => void
): IDataColumn<IListImport>[] {
  return [
    {
      id: 'open',
      header: <span className="sr-only">Open</span>,
      label: 'Open',
      className: 'w-0 whitespace-nowrap',
      cell: (entry) => (
        <RowOpens
          name={entry.fileName}
          onPane={() => beside(entry)}
          to={importPath(entry.id)}
          tabWords={`Open ${entry.fileName} in a new tab`}
        />
      )
    },
    {
      id: 'file',
      header: 'File',
      className: 'font-medium break-words',
      cell: (entry) => entry.fileName
    },
    {
      id: 'survey',
      header: head(IMPORT_FILTERS[0]),
      label: 'Survey',
      cell: (entry) => listSurveyLabel(entry.survey)
    },
    {
      id: 'state',
      header: head(IMPORT_FILTERS[1]),
      label: 'State',
      cell: (entry) => <ImportStateChip state={entry.state} />
    },
    { id: 'rows', header: 'Rows', numeric: true, cell: (entry) => counted(entry, entry.counts.rows) },
    { id: 'matched', header: 'Matched', numeric: true, cell: (entry) => counted(entry, entry.counts.matched) },
    { id: 'changed', header: 'Changed', numeric: true, cell: (entry) => counted(entry, entry.counts.changed) },
    { id: 'held', header: 'Held', numeric: true, cell: (entry) => counted(entry, entry.counts.held) },
    { id: 'errors', header: 'Errors', numeric: true, cell: (entry) => counted(entry, entry.counts.errors) },
    { id: 'by', header: 'By', cell: (entry) => entry.createdBy.email },
    {
      id: 'when',
      header: 'When',
      className: 'whitespace-nowrap',
      cell: (entry) => formatDateTime(entry.createdAt)
    }
  ];
}

/** The imports and an import, as one area: the list, or an import beside it. */
export function AdminImportsArea() {
  return (
    <ListArea
      param="importId"
      list={TEAM_PATHS.imports}
      paneTitle="Imports"
      pane={(search, selected) => <ImportsPane search={search} selected={selected} />}
    />
  );
}

/** The imports as the pane beside one draws them — the list's query and window in the viewer's
 *  Per page, its page turned here. */
function ImportsPane({ search, selected }: { search: string; selected: string }) {
  const [perPage] = usePerPage();
  const { shown, pager, status } = usePaneRead(search, 'No import yet.', (params, page, signal) => {
    const window = windowOf(params, perPage);
    return readWindow(params, page, perPage, page === window.page ? window.pages : 1, signal);
  });
  return (
    <SplitList
      label="Imports"
      selected={shown?.items.some((entry) => entry.id === selected) ? selected : null}
      items={
        shown?.items.map((entry) => ({
          key: entry.id,
          to: importPath(entry.id),
          state: { search, list: true } satisfies ICardLinkState,
          primary: entry.fileName,
          secondary: `${listSurveyLabel(entry.survey)} · ${formatDateTime(entry.createdAt)}`,
          trailing: <ImportStateChip state={entry.state} />
        })) ?? null
      }
      status={status}
      footer={pager}
    />
  );
}
